import { z } from "zod";
import { platformAdminRoleSchema } from "./enums";

/** Stable error codes. Clients branch on these and translate them. See docs/architecture/api.md. */
export const APP_CODES = [
	"UNAUTHENTICATED",
	"FORBIDDEN",
	"NOT_FOUND",
	"VALIDATION",
	"CONFLICT",
	"ITEM_UNAVAILABLE",
	"PRICE_CHANGED",
	"ORDERING_CLOSED",
	"SUBSCRIPTION_SUSPENDED",
	"RATE_LIMITED",
	"UPGRADE_REQUIRED",
	"INTERNAL",
] as const;
export type AppCode = (typeof APP_CODES)[number];

/**
 * Most calls in one tRPC batch. The API refuses larger batches (400), so
 * one request cannot carry thousands of guesses; the client link splits at
 * this size (P12-T06).
 */
export const TRPC_MAX_BATCH = 50;

/**
 * Largest request body the API reads on `/api/trpc` (413 above it). The
 * biggest real input is an admin's reviewed menu import draft, well below.
 */
export const API_MAX_BODY_BYTES = 2 * 1024 * 1024;

/** Headers the clients send. */
export const HEADERS = {
	requestId: "x-request-id",
	restaurantId: "x-restaurant-id",
	appVersion: "x-app-version",
	tenantHost: "x-tenant-host",
	/** An admin support session. See docs/architecture/auth-tenancy.md. */
	impersonation: "x-impersonation",
	/**
	 * Sent as `1` with every public mutation (the storefront's calls). A
	 * cross-site form cannot set it, so the guest cookie cannot be abused.
	 */
	csrf: "x-lm-csrf",
	/**
	 * The staff app's own device ID (`luckymenu.deviceId`, a UUID). Print jobs
	 * made by a call remember it, so that device gets the first 2 seconds to
	 * print them (printing.md). Optional.
	 */
	deviceId: "x-device-id",
	/**
	 * A quick switch on a shared device (P6-T11): the token from
	 * `staff.tenancy.switchPerson`. Calls then act as that member.
	 */
	staffSwitch: "x-staff-switch",
	/**
	 * The Android app's build number (`versionCode`). Below the server's
	 * minimum (`ANDROID_MIN_BUILD`) staff calls answer `UPGRADE_REQUIRED` (P6-T13).
	 */
	appBuild: "x-app-build",
} as const;

export const pingOutputSchema = z.object({
	pong: z.literal(true),
	time: z.iso.datetime(),
});

const userFields = {
	id: z.uuid(),
	name: z.string().nullable(),
	email: z.string().nullable(),
	avatarUrl: z.string().nullable(),
};

/** `staff.identity.me` */
export const meSchema = z.object({
	...userFields,
	isPlatformAdmin: z.boolean(),
});
export type Me = z.infer<typeof meSchema>;

/** `admin.identity.me` */
export const adminMeSchema = z.object({
	...userFields,
	role: platformAdminRoleSchema,
});
export type AdminMe = z.infer<typeof adminMeSchema>;

/** A row on the admin app's Admins page. */
export const platformAdminSchema = z.object({
	userId: z.uuid(),
	name: z.string().nullable(),
	email: z.string().nullable(),
	avatarUrl: z.string().nullable(),
	role: platformAdminRoleSchema,
	createdAt: z.iso.datetime(),
});
export type PlatformAdminEntry = z.infer<typeof platformAdminSchema>;

/** The person must have signed in once, with this email verified. */
export const addAdminInputSchema = z.object({
	email: z
		.email()
		.max(254)
		.transform((e) => e.toLowerCase()),
	role: platformAdminRoleSchema,
});

export const removeAdminInputSchema = z.object({ userId: z.uuid() });
